/**
 * Task email preferences per board and per General section
 * (GET/PUT /api/me/task-notification-preferences, Gluska-Forge-API #107).
 *
 * Three levels, most specific wins: a General section's override, then the
 * board's, then the global value, then on. `overrides` holds only what was set
 * explicitly (a missing key inherits); `effective` is the resolved value the
 * server computed. Each matrix cell is tri-state: on, off, or inherit. The
 * global level has no parent, so it is on or off (clearing it means on).
 *
 * Pure, no React: the page and the node tests share it.
 */

export type EmailScope = "global" | "board" | "section"

export interface EmailPrefType {
  key: string
  label: string
  description?: string
  scopes: EmailScope[]
}

export type PrefMap = Record<string, boolean>

export interface EmailPrefSection {
  id: number
  name: string
  emoji: string | null
  color: string | null
  overrides: Partial<PrefMap>
  effective: PrefMap
}

export interface EmailPrefBoard {
  id: number
  key: string
  name: string
  color: string | null
  is_default: boolean
  overrides: Partial<PrefMap>
  effective: PrefMap
  sections?: EmailPrefSection[]
}

export interface TaskEmailPreferences {
  types: EmailPrefType[]
  global: Partial<PrefMap>
  boards: EmailPrefBoard[]
}

export interface TaskEmailPreferencesBody {
  data: TaskEmailPreferences
}

/** One column (or mobile group) of the matrix. */
export type EmailPrefTarget =
  | { scope: "global" }
  | { scope: "board"; board: string }
  | { scope: "section"; section: string }

export type CellState = "on" | "off" | "inherit"

export interface CellView {
  state: CellState
  /** What the email actually does: the explicit value, or the inherited one. */
  effective: boolean
  /** Where an inherited value comes from, for the tooltip. */
  inheritsFrom: string | null
}

export interface TaskEmailPreferencesPut {
  global?: Record<string, boolean | null>
  boards?: { key: string; overrides: Record<string, boolean | null> }[]
  sections?: { name: string; overrides: Record<string, boolean | null> }[]
}

/** The one place the `{ data }` envelope is opened. */
export function readTaskEmailPreferences(body: TaskEmailPreferencesBody): TaskEmailPreferences {
  const data = body?.data
  return {
    types: Array.isArray(data?.types) ? data.types : [],
    global: data?.global ?? {},
    boards: Array.isArray(data?.boards) ? data.boards : [],
  }
}

function explicit(overrides: Partial<PrefMap> | undefined, key: string): boolean | undefined {
  const value = overrides?.[key]
  return typeof value === "boolean" ? value : undefined
}

/** Global, with the server's default for anything unset: on. */
export function globalValue(prefs: TaskEmailPreferences, key: string): boolean {
  return explicit(prefs.global, key) ?? true
}

export function findBoard(prefs: TaskEmailPreferences, key: string): EmailPrefBoard | undefined {
  return prefs.boards.find((b) => b.key === key)
}

export function findSection(
  prefs: TaskEmailPreferences,
  name: string,
): { board: EmailPrefBoard; section: EmailPrefSection } | undefined {
  for (const board of prefs.boards) {
    const section = board.sections?.find((s) => s.name === name)
    if (section) return { board, section }
  }
  return undefined
}

/** Whether a type can be set at this level (project_completed is global-only). */
export function typeAllows(type: EmailPrefType, target: EmailPrefTarget): boolean {
  return type.scopes.includes(target.scope)
}

/**
 * Section → board → global → on, computed locally from the overrides alone.
 * Used for the optimistic state between a click and the server's answer.
 */
export function resolveValue(prefs: TaskEmailPreferences, target: EmailPrefTarget, key: string): boolean {
  if (target.scope === "global") return globalValue(prefs, key)
  if (target.scope === "board") {
    const board = findBoard(prefs, target.board)
    return explicit(board?.overrides, key) ?? globalValue(prefs, key)
  }
  const found = findSection(prefs, target.section)
  if (!found) return globalValue(prefs, key)
  return explicit(found.section.overrides, key) ?? explicit(found.board.overrides, key) ?? globalValue(prefs, key)
}

/** What one cell shows. */
export function cellView(prefs: TaskEmailPreferences, target: EmailPrefTarget, key: string): CellView {
  if (target.scope === "global") {
    const value = globalValue(prefs, key)
    return { state: value ? "on" : "off", effective: value, inheritsFrom: null }
  }
  if (target.scope === "board") {
    const board = findBoard(prefs, target.board)
    const own = explicit(board?.overrides, key)
    if (own !== undefined) return { state: own ? "on" : "off", effective: own, inheritsFrom: null }
    return {
      state: "inherit",
      effective: board?.effective?.[key] ?? globalValue(prefs, key),
      inheritsFrom: "Global",
    }
  }
  const found = findSection(prefs, target.section)
  const own = explicit(found?.section.overrides, key)
  if (own !== undefined) return { state: own ? "on" : "off", effective: own, inheritsFrom: null }
  const fromBoard = found && explicit(found.board.overrides, key) !== undefined
  return {
    state: "inherit",
    effective: found?.section.effective?.[key] ?? resolveValue(prefs, target, key),
    inheritsFrom: fromBoard && found ? found.board.name : "Global",
  }
}

/**
 * What a cell would resolve to if it inherited, and from where: the menu
 * shows it beside "Inherit" so choosing it is never a guess. Global has no
 * level above; its "inherit" (null) means on.
 */
export function parentValue(prefs: TaskEmailPreferences, target: EmailPrefTarget, key: string): { value: boolean; from: string } {
  if (target.scope === "global") return { value: true, from: "the default" }
  if (target.scope === "board") return { value: globalValue(prefs, key), from: "Global" }
  const found = findSection(prefs, target.section)
  const boardValue = explicit(found?.board.overrides, key)
  if (found && boardValue !== undefined) return { value: boardValue, from: found.board.name }
  return { value: globalValue(prefs, key), from: "Global" }
}

function stateValue(state: CellState): boolean | null {
  return state === "inherit" ? null : state === "on"
}

/**
 * The partial PUT body for one cell change. `inherit` sends null, which
 * clears the value (global back to on; a board or section back to inherit).
 */
export function buildPutBody(target: EmailPrefTarget, key: string, state: CellState): TaskEmailPreferencesPut {
  const value = stateValue(state)
  if (target.scope === "global") return { global: { [key]: value } }
  if (target.scope === "board") return { boards: [{ key: target.board, overrides: { [key]: value } }] }
  return { sections: [{ name: target.section, overrides: { [key]: value } }] }
}

function withOverride(overrides: Partial<PrefMap>, key: string, value: boolean | null): Partial<PrefMap> {
  const next = { ...overrides }
  if (value === null) delete next[key]
  else next[key] = value
  return next
}

/** Recomputes every `effective` map from global and the overrides. */
export function recomputeEffective(prefs: TaskEmailPreferences): TaskEmailPreferences {
  const taskKeys = prefs.types.filter((t) => t.scopes.includes("board")).map((t) => t.key)
  const resolve = (target: EmailPrefTarget) =>
    Object.fromEntries(taskKeys.map((key) => [key, resolveValue(prefs, target, key)])) as PrefMap
  return {
    ...prefs,
    boards: prefs.boards.map((board) => ({
      ...board,
      effective: resolve({ scope: "board", board: board.key }),
      sections: board.sections?.map((section) => ({
        ...section,
        effective: resolve({ scope: "section", section: section.name }),
      })),
    })),
  }
}

/** The state the matrix shows right after a click, before the server answers. */
export function applyCellChange(
  prefs: TaskEmailPreferences,
  target: EmailPrefTarget,
  key: string,
  state: CellState,
): TaskEmailPreferences {
  const value = stateValue(state)
  let next: TaskEmailPreferences
  if (target.scope === "global") {
    // Clearing global means on: store nothing, and globalValue reads it as on.
    next = { ...prefs, global: withOverride(prefs.global, key, value) }
  } else if (target.scope === "board") {
    next = {
      ...prefs,
      boards: prefs.boards.map((b) =>
        b.key === target.board ? { ...b, overrides: withOverride(b.overrides, key, value) } : b,
      ),
    }
  } else {
    next = {
      ...prefs,
      boards: prefs.boards.map((b) =>
        b.sections?.some((s) => s.name === target.section)
          ? {
              ...b,
              sections: b.sections.map((s) =>
                s.name === target.section ? { ...s, overrides: withOverride(s.overrides, key, value) } : s,
              ),
            }
          : b,
      ),
    }
  }
  return recomputeEffective(next)
}

/** The states a cell offers: global has nothing above it to inherit from. */
export function cellOptions(target: EmailPrefTarget): CellState[] {
  return target.scope === "global" ? ["on", "off"] : ["on", "off", "inherit"]
}

/** Short words for a state, for labels and tooltips. */
export function describeCell(view: CellView): string {
  if (view.state === "inherit") return `Inherits ${view.effective ? "on" : "off"} from ${view.inheritsFrom ?? "Global"}`
  return view.state === "on" ? "On" : "Off"
}
